/**
 * Collapsible call-guide panel used inside the Client 360 page.
 * Receives pre-loaded portfolio/case/interaction data as props to
 * avoid duplicate fetches. Owns timer + call-logger form state locally.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import interactionsService, {
  type ClientCase,
  type Interaction,
  type InteractionKind,
  type InteractionOutcome,
} from '../../services/interactions.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { calendarDate } from '../client/enteringFund.helpers';
import { completeAfter } from '../client/openTerms.helpers';
import { humanize } from '../../lib/stringCase';
import { colors, gradients } from '../../styles/theme';
import WithdrawalRetentionGuide from './WithdrawalRetentionGuide';
import {
  AWAITING_ALLOCATION_TEXT,
  DESCRIBE_SERIES_TEXT,
  EXIT_TERMS_TEXT,
  ONBOARDING_HELP_SHORT_TEXTS,
  SALES_CALL_DISCLAIMER_TEXT,
  SIGN_IN_HELP_TEXTS,
  TAX_QUESTIONS_TEXT,
} from './callGuideTexts';
import type { PortfolioSummary } from '../../types/finance';

// ─── Helpers ──────────────────────────────────────────────────────

function elapsed(startMs: number): string {
  const s = Math.floor((Date.now() - startMs) / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

function kindLabel(k: string): string {
  const MAP: Record<string, string> = {
    phone_outbound: 'Outbound call',
    phone_inbound: 'Inbound call',
    email_outbound: 'Email sent',
    email_inbound: 'Email received',
  };
  return MAP[k] ?? k;
}

function outcomeLabel(o: string): string {
  const MAP: Record<string, string> = {
    connected: 'Connected',
    voicemail_left: 'Voicemail left',
    no_answer: 'No answer',
    busy: 'Busy',
    wrong_number: 'Wrong number',
    client_hung_up: 'Client hung up',
    callback_scheduled: 'Callback scheduled',
  };
  return MAP[o] ?? o;
}

// ─── Accordion section ────────────────────────────────────────────

interface SectionProps {
  readonly title: string;
  readonly badge?: string;
  readonly children: React.ReactNode;
  readonly defaultOpen?: boolean;
}

function Section({ title, badge, children, defaultOpen = false }: SectionProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: open ? `${colors.navy}08` : colors.surface, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: colors.ink, textAlign: 'left', transition: 'background 0.12s' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {title}
          {badge && (
            <span style={{ fontSize: 10, fontWeight: 500, background: colors.gold, color: colors.navy, borderRadius: 4, padding: '1px 6px' }}>{badge}</span>
          )}
        </span>
        <span style={{ fontSize: 11, color: colors.textMuted }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '12px 14px', background: colors.surface }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Talking-point item ───────────────────────────────────────────

function TalkingPoint({ text }: { readonly text: string }): JSX.Element {
  const [checked, setChecked] = useState(false);
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '5px 0', cursor: 'pointer', opacity: checked ? 0.45 : 1, transition: 'opacity 0.15s' }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={e => setChecked(e.target.checked)}
        style={{ marginTop: 3, accentColor: colors.navy, flexShrink: 0 }}
      />
      <span style={{ fontSize: 13, color: colors.inkSoft, textDecoration: checked ? 'line-through' : 'none' }}>
        {text}
      </span>
    </label>
  );
}

// ─── Panel ────────────────────────────────────────────────────────

interface CallGuidePanelProps {
  readonly clientId: string;
  readonly clientName: string;
  readonly summary: PortfolioSummary | null;
  readonly openCases: ClientCase[];
  readonly lastInteraction: Interaction | null;
  readonly onCallLogged: () => void;
  readonly onClose: () => void;
}

export default function CallGuidePanel({
  clientId,
  clientName,
  summary,
  openCases,
  lastInteraction,
  onCallLogged,
  onClose,
}: CallGuidePanelProps): JSX.Element {
  const [selectedCaseId, setSelectedCaseId] = useState(() => openCases[0]?.case_id ?? '');
  const [callKind, setCallKind] = useState<InteractionKind>('phone_outbound');
  const [outcome, setOutcome] = useState<InteractionOutcome>('connected');
  const [notes, setNotes] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [commitment, setCommitment] = useState('');
  const [logError, setLogError] = useState<string | null>(null);
  const [logSuccess, setLogSuccess] = useState(false);
  const [logging, setLogging] = useState(false);

  const [timerStart, setTimerStart] = useState<number | null>(null);
  const [timerDisplay, setTimerDisplay] = useState('00:00');
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startTimer = (): void => {
    if (timerStart !== null) return;
    const start = Date.now();
    setTimerStart(start);
    timerRef.current = setInterval(() => setTimerDisplay(elapsed(start)), 1000);
  };

  const stopTimer = (): void => {
    if (timerRef.current) clearInterval(timerRef.current);
  };

  useEffect(() => () => stopTimer(), []);

  useEffect(() => {
    if (openCases.length > 0 && selectedCaseId === '') {
      setSelectedCaseId(openCases[0].case_id);
    }
  }, [openCases, selectedCaseId]);

  const handleLogCall = useCallback(async (): Promise<void> => {
    if (!selectedCaseId) { setLogError('Select a case to associate this interaction with.'); return; }
    setLogging(true);
    setLogError(null);
    try {
      const { id: interactionId } = await interactionsService.start(selectedCaseId, {
        kind: callKind,
        started_at: timerStart ? new Date(timerStart).toISOString() : undefined,
      });
      await interactionsService.update(interactionId, {
        outcome,
        ended_at: new Date().toISOString(),
        notes: notes || undefined,
        next_action_required: nextAction || undefined,
        commitment_made: commitment || undefined,
      });
      stopTimer();
      setLogSuccess(true);
      setNotes('');
      setNextAction('');
      setCommitment('');
      setTimerStart(null);
      setTimerDisplay('00:00');
      onCallLogged();
    } catch (e: unknown) {
      setLogError(getApiErrorMessage(e, 'Could not log the interaction.'));
    } finally {
      setLogging(false);
    }
  }, [selectedCaseId, callKind, outcome, notes, nextAction, commitment, timerStart, onCallLogged]);

  const ccy = summary?.upcoming_maturities[0]?.currency_code ?? 'MXN';
  const canLog = !logging && openCases.length > 0;

  return (
    <div style={{ background: colors.surface, border: `2px solid ${colors.navy}`, borderRadius: 14, overflow: 'hidden', height: '100%' }}>
      {/* Panel header */}
      <div style={{ background: gradients.navy, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Call Guide</div>
        <button
          type="button"
          onClick={onClose}
          style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}
          aria-label="Close call guide"
        >
          ✕
        </button>
      </div>

      <div style={{ padding: '14px 16px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 'calc(100vh - 220px)' }}>

        {/* Timer */}
        <div style={{ background: colors.surfaceMuted ?? colors.border, borderRadius: 10, padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 24, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: timerStart === null ? colors.textMuted : colors.navy }}>
              {timerDisplay}
            </div>
            {timerStart !== null && (
              <span style={{ fontSize: 10, fontWeight: 700, color: colors.success, background: colors.successBg, borderRadius: 4, padding: '2px 6px' }}>LIVE</span>
            )}
          </div>
          <button
            type="button"
            onClick={timerStart === null ? startTimer : stopTimer}
            style={{ padding: '6px 14px', borderRadius: 7, border: 'none', background: timerStart === null ? gradients.navy : colors.dangerBg, color: timerStart === null ? '#fff' : colors.danger, fontWeight: 600, fontSize: 12, cursor: 'pointer' }}
          >
            {timerStart === null ? 'Start timer' : 'Stop'}
          </button>
        </div>

        {/* Last contact context */}
        {lastInteraction && (
          <div style={{ fontSize: 12, color: colors.inkSoft, padding: '6px 10px', background: `${colors.navy}08`, borderRadius: 8, borderLeft: `3px solid ${colors.navy}` }}>
            <span style={{ fontWeight: 600, color: colors.ink }}>
              {kindLabel(lastInteraction.kind)} — {outcomeLabel(lastInteraction.outcome)}
            </span>
            {' · '}{formatDateTime(lastInteraction.started_at)}
            {lastInteraction.next_action_required && (
              <div style={{ marginTop: 4, color: colors.warning }}>
                Pending: {lastInteraction.next_action_required}
              </div>
            )}
          </div>
        )}

        {/* Upcoming maturities context */}
        {summary && summary.upcoming_maturities.length > 0 && (
          <div style={{ fontSize: 12, padding: '6px 10px', background: `${colors.gold}12`, borderRadius: 8, borderLeft: `3px solid ${colors.gold}` }}>
            <div style={{ fontWeight: 600, color: colors.ink, marginBottom: 2 }}>Term ending</div>
            <div style={{ color: colors.inkSoft }}>
              {summary.upcoming_maturities[0].product_name} · {completeAfter(summary.upcoming_maturities[0].maturity_date)} · {formatMoney(summary.upcoming_maturities[0].amount, ccy)}
            </div>
          </div>
        )}

        {/* Withdrawal retention — live release date for an open withdrawal, or
            the static cooling-off script on a first withdrawal; retention
            talking-points always show. */}
        <WithdrawalRetentionGuide clientId={clientId} />

        {/* Talking points */}
        <Section title="Opening" defaultOpen>
          <TalkingPoint text={`Confirm you're speaking with ${clientName} and they have a good moment.`} />
          <TalkingPoint text="Before any amount or status: they tell you their full name and date of birth; compare with the file, never read it back. No match: no information, and log the call." />
          <TalkingPoint text="State the purpose of the call in one sentence." />
          <TalkingPoint text={SALES_CALL_DISCLAIMER_TEXT} />
          {lastInteraction?.next_action_required && (
            <TalkingPoint text={`Follow up: "${lastInteraction.next_action_required}"`} />
          )}
        </Section>

        <Section title="Onboarding help">
          {ONBOARDING_HELP_SHORT_TEXTS.map((t) => <TalkingPoint key={t} text={t} />)}
        </Section>

        <Section title="Summary review" badge={summary ? `${summary.active_products_count} products` : undefined}>
          <TalkingPoint text="Review their current balance and confirm it matches their expectations." />
          {summary && summary.upcoming_maturities.length > 0 && (
            <TalkingPoint text={`Term ending: the current term in ${summary.upcoming_maturities[0].product_name} is complete after ${calendarDate(summary.upcoming_maturities[0].maturity_date)}. The next term starts by itself — ask what they intend.`} />
          )}
          <TalkingPoint text="Review the results shared with them to date. They are their share of what each product actually earned, never a fixed rate." />
          <TalkingPoint text={AWAITING_ALLOCATION_TEXT} />
          {summary && summary.active_commitments_count > 0 && (
            <TalkingPoint text={`${summary.active_commitments_count} active commitment(s) — confirm recurring schedule is still correct.`} />
          )}
        </Section>

        <Section title="Needs discovery">
          <TalkingPoint text="What do they want their money to do in the next 12 months?" />
          <TalkingPoint text="Liquidity need or risk tolerance changed since the last call? If so, note what changed." />
          <TalkingPoint text="Money to contribute beyond their emergency reserve?" />
          <TalkingPoint text="A large expense coming that changes what they can contribute?" />
        </Section>

        <Section title="Series open to them">
          <TalkingPoint text={DESCRIBE_SERIES_TEXT} />
          <TalkingPoint text="Walk them through the Simulator in their own portal (Simulator tab) with their amount. Its figures are for informational purposes and are not promised rates: they receive their share of what the Series actually earns." />
          <TalkingPoint text={EXIT_TERMS_TEXT} />
          <TalkingPoint text="If interested, use '+ Propose a Series'; they sign and contribute from their portal. If it refuses one, do not offer it or explain why." />
          <TalkingPoint text={TAX_QUESTIONS_TEXT} />
        </Section>

        <Section title="Objections">
          <TalkingPoint text="'Rates too low' — back to their goals and horizon. They are a partner and share the Series' real result; never compare it with bank accounts or government instruments." />
          <TalkingPoint text="'Need liquidity' — Series with shorter terms; when a term ends the next starts by itself unless they withdraw. Check their reserve elsewhere: capital withdrawn before its term ends earns nothing for that term." />
          <TalkingPoint text="'Not sure about platform' — walk through their Summary and statements." />
          <TalkingPoint text="'I'll think about it' — agree a callback date; note as next action." />
        </Section>

        <Section title="Sign-in help">
          {SIGN_IN_HELP_TEXTS.map((t) => <TalkingPoint key={t} text={t} />)}
        </Section>

        <Section title="Closing">
          <TalkingPoint text="Summarise what was agreed and next steps." />
          <TalkingPoint text="Confirm best callback time." />
          <TalkingPoint text="Log this interaction before moving to the next client." />
        </Section>

        {/* ── Call logger ── */}
        <div style={{ border: `1px solid ${colors.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ background: `${colors.navy}10`, padding: '8px 12px', fontSize: 12, fontWeight: 700, color: colors.navy, letterSpacing: '0.02em' }}>
            Log this interaction
          </div>
          <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: 10 }}>

            {logSuccess && (
              <div style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, borderRadius: 7, padding: '8px 12px', color: colors.successText, fontSize: 12 }}>
                Logged successfully.
              </div>
            )}
            {logError && (
              <div style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, borderRadius: 7, padding: '8px 12px', color: colors.danger, fontSize: 12 }}>
                {logError}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label htmlFor="callguide-call-type" style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Type</label>
                <select
                  id="callguide-call-type"
                  value={callKind}
                  onChange={e => setCallKind(e.target.value as InteractionKind)}
                  style={{ width: '100%', padding: '7px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12, background: colors.surface }}
                >
                  <option value="phone_outbound">Outbound call</option>
                  <option value="phone_inbound">Inbound call</option>
                  <option value="email_outbound">Email sent</option>
                  <option value="email_inbound">Email received</option>
                </select>
              </div>
              <div>
                <label htmlFor="callguide-call-outcome" style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Outcome</label>
                <select
                  id="callguide-call-outcome"
                  value={outcome}
                  onChange={e => setOutcome(e.target.value as InteractionOutcome)}
                  style={{ width: '100%', padding: '7px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12, background: colors.surface }}
                >
                  <option value="connected">Connected</option>
                  <option value="voicemail_left">Voicemail left</option>
                  <option value="no_answer">No answer</option>
                  <option value="busy">Busy</option>
                  <option value="wrong_number">Wrong number</option>
                  <option value="client_hung_up">Client hung up</option>
                  <option value="callback_scheduled">Callback scheduled</option>
                </select>
              </div>
            </div>

            {openCases.length > 0 ? (
              <div>
                <label htmlFor="callguide-call-case" style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Case</label>
                <select
                  id="callguide-call-case"
                  value={selectedCaseId}
                  onChange={e => setSelectedCaseId(e.target.value)}
                  style={{ width: '100%', padding: '7px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12, background: colors.surface }}
                >
                  {openCases.map(c => (
                    <option key={c.case_id} value={c.case_id}>
                      {c.workflow_name} — {humanize(c.current_state_slug)}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: colors.textMuted, padding: '6px 8px', background: colors.border, borderRadius: 6 }}>
                No open cases — open a case to link this interaction.
              </div>
            )}

            <div>
              <label htmlFor="callguide-call-notes" style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Notes</label>
              <textarea
                id="callguide-call-notes"
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={3}
                maxLength={10000}
                placeholder="Key points, concerns, context…"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12, resize: 'vertical', background: colors.surface }}
              />
            </div>

            <div>
              <label htmlFor="callguide-call-commitment" style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Commitment made</label>
              <input
                id="callguide-call-commitment"
                type="text"
                value={commitment}
                onChange={e => setCommitment(e.target.value)}
                maxLength={500}
                placeholder="e.g. Client will transfer MXN 100K by Friday"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12 }}
              />
            </div>

            <div>
              <label htmlFor="callguide-call-next-action" style={{ fontSize: 10, fontWeight: 600, color: colors.textMuted, display: 'block', marginBottom: 4, textTransform: 'uppercase' }}>Next action</label>
              <input
                id="callguide-call-next-action"
                type="text"
                value={nextAction}
                onChange={e => setNextAction(e.target.value)}
                maxLength={500}
                placeholder="e.g. Call back Thursday to confirm transfer"
                style={{ width: '100%', boxSizing: 'border-box', padding: '7px 8px', borderRadius: 6, border: `1px solid ${colors.border}`, fontSize: 12 }}
              />
            </div>

            <button
              type="button"
              onClick={() => handleLogCall()}
              disabled={!canLog}
              style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: canLog ? gradients.navy : colors.border, color: canLog ? '#fff' : colors.textMuted, fontWeight: 600, fontSize: 13, cursor: canLog ? 'pointer' : 'not-allowed' }}
            >
              {logging ? 'Logging…' : 'Log interaction'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
